// Per-module navigation header — independent from main landing nav.
// Header bar background uses module primary colour; logo on left, menu items white.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function ModuleNav({ module, theme, items = [] }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  // Sign in dynamic
  const signedIn = !!user;
  const isSuperAdmin = user?.role === 'super_admin';
  const ctaTarget = signedIn
    ? (isSuperAdmin ? '/control-panel' : `/dashboard/${module.slug}`)
    : '/login';
  const ctaLabel = signedIn ? (isSuperAdmin ? 'Control Panel' : 'Dashboard') : 'Sign In';

  return (
    <header
      className="sticky top-0 z-40 w-full border-b"
      style={{ background: theme.primary, borderColor: 'rgba(255,255,255,0.08)' }}
      data-testid={`module-nav-${module.slug}`}
    >
      <div className="container-x flex items-center justify-between h-16">
        {/* Logo — far left, no background fill */}
        <Link to={`/m/${module.slug}`} className="flex items-center gap-3" data-testid="module-logo">
          <img src={module.logo} alt={module.short} className="h-9 w-9 object-contain" />
          <div className="leading-tight">
            <div className="font-display text-sm font-bold tracking-tight" style={{ color: theme.textOnPrimary }}>
              {module.short}
            </div>
            <div className="text-[9px] uppercase tracking-[0.22em] hidden sm:block" style={{ color: 'rgba(255,255,255,0.55)' }}>
              ATTP NEXUS
            </div>
          </div>
        </Link>

        {/* Menu items */}
        <nav className="hidden lg:flex items-center gap-7" data-testid="module-menu">
          {items.map((it) => (
            it.dropdown ? (
              <DropdownItem key={it.label} item={it} theme={theme} />
            ) : (
              <a
                key={it.label}
                href={it.href}
                className="text-sm font-medium tracking-tight transition-opacity hover:opacity-70"
                style={{ color: theme.textOnPrimary }}
                data-testid={`module-link-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {it.label}
              </a>
            )
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/" className="text-xs uppercase tracking-wider transition-opacity hover:opacity-70" style={{ color: 'rgba(255,255,255,0.7)' }}>
            ← ATTP NEXUS
          </Link>
          <Link
            to={ctaTarget}
            className="px-5 py-2 rounded-md font-semibold text-sm transition-all hover:scale-105"
            style={{ background: theme.accent, color: theme.primary }}
            data-testid="module-cta"
          >
            {ctaLabel}
          </Link>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} style={{ color: theme.textOnPrimary }}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile */}
      {open && (
        <div className="lg:hidden border-t" style={{ background: theme.primary, borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="container-x py-4 flex flex-col gap-3">
            {items.map((it) => (
              it.dropdown ? (
                <details key={it.label}>
                  <summary className="text-sm py-1 cursor-pointer font-medium" style={{ color: theme.textOnPrimary }}>{it.label}</summary>
                  <div className="pl-4 flex flex-col gap-1 mt-1">
                    {it.dropdown.map((d) => (
                      <a key={d.label} href={d.href} className="text-xs py-1" style={{ color: 'rgba(255,255,255,0.75)' }} onClick={() => setOpen(false)}>{d.label}</a>
                    ))}
                  </div>
                </details>
              ) : (
                <a key={it.label} href={it.href} className="text-sm py-1" style={{ color: theme.textOnPrimary }} onClick={() => setOpen(false)}>{it.label}</a>
              )
            ))}
            <Link to="/" className="text-xs uppercase tracking-wider py-1" style={{ color: 'rgba(255,255,255,0.55)' }} onClick={() => setOpen(false)}>
              ← ATTP NEXUS
            </Link>
            <Link to={ctaTarget} className="mt-2 px-4 py-2 rounded-md font-semibold text-sm text-center" style={{ background: theme.accent, color: theme.primary }} onClick={() => setOpen(false)}>{ctaLabel}</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function DropdownItem({ item, theme }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="text-sm font-medium tracking-tight transition-opacity hover:opacity-70" style={{ color: theme.textOnPrimary }}>
        {item.label} ▾
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-md shadow-xl border border-neutral-200 py-2 z-50">
          {item.dropdown.map((d) => (
            <a key={d.label} href={d.href} className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50">{d.label}</a>
          ))}
        </div>
      )}
    </div>
  );
}
